import { useMemo } from "react";

import type { Amenity } from "@/lib/gql/__generated__/graphql";

import type { ForArrayType } from "@/components/features/_model/types/auto-generator-for-view-model";
import {
	AMENITIES,
	BATH_ITEMS,
	FACILITIES,
} from "@/components/features/items/edit/_shared/constants/amenities-and-facilities";

import type { FormOptionType } from "@/components/ui/form/_types/type";

import { useProductRoomFormModelContext } from "../../../../context";

import type { CheckedState } from "@radix-ui/react-checkbox";

export const useRoomAmenities = () => {
	const { viewModel, onSetViewModel } = useProductRoomFormModelContext();
	const { amenities } = viewModel;

	const rawAmenities = useMemo(() => amenities.value.map((item) => item.value), [amenities.value]);

	const onSetAmenities = (checked: CheckedState, item: FormOptionType) => {
		if (checked) {
			if (amenities.value.length) {
				const newValue = {
					...amenities,
					value: [
						...amenities.value, //
						{
							isEdited: true,
							value: item.value as Amenity,
						},
					],
				} satisfies ForArrayType<Amenity[]>;

				onSetViewModel([
					{
						key: "amenities",
						value: newValue,
					},
				]);
			} else {
				const newValue = {
					...amenities,
					value: [
						{
							isEdited: true,
							value: item.value as Amenity,
						},
					],
				} satisfies ForArrayType<Amenity[]>;

				onSetViewModel([
					{
						key: "amenities",
						value: newValue,
					},
				]);
			}
		} else {
			const newValue = amenities.value.filter((value) => value.value !== (item.value as Amenity));

			onSetViewModel([
				{
					key: "amenities",
					value: {
						...amenities,
						value: newValue,
					} satisfies ForArrayType<Amenity[]>,
				},
			]);
		}
	};

	const roomAmenities = AMENITIES.map((item) => {
		return {
			id: "room_amenities_field_" + item.key,
			label: item.key,
			value: item.value,
		} satisfies FormOptionType;
	});

	const roomFacilities = FACILITIES.map((item) => {
		return {
			id: "room_amenities_facilities_" + item.key,
			label: item.key,
			value: item.value,
		} satisfies FormOptionType;
	});

	const bathRoomAmenities = BATH_ITEMS.map((item) => {
		return {
			id: "room_amenities_bath_items_" + item.key,
			label: item.key,
			value: item.value,
		} satisfies FormOptionType;
	});

	const isEdited = amenities.isEdited;
	const hasError = amenities.hasError;

	return {
		isEdited,
		hasError,

		roomAmenities,
		roomFacilities,
		bathRoomAmenities,

		rawAmenities,
		amenities,
		onSetAmenities,
	};
};
